import type { StepId } from '../types';

export interface StepDefinition {
  id: StepId;
  title: string;
  /** One-sentence goal shown under the step title (drives the target-icon line). */
  goal: string;
  /** True for the branch parent (rest_branch) so sidebar can render branch indicators. */
  isBranchParent?: boolean;
}

/**
 * Canonical step ordering. Mirrors the dependency chain:
 *
 *   1. Pre-flight & specs  →  baseline bow truth-checking
 *   2. Cam timing          →  must be locked before any tear-based tuning
 *   3. Tiller              →  affects everything downstream
 *   4. Center shot         →  starting point for paper / walk-back
 *   5. Nocking + D-loop    →  needs to be in place before paper
 *   6. Rest type branch    →  user splits paths here
 *   7. Paper tune          →  micro-tunes center shot + nocking
 *   8. Bareshaft           →  unmasks fletching-hidden errors
 *   9. Walk-back           →  final left/right at distance
 *   10. Broadhead group    →  the practical "is it really tuned" test
 *   11. Final report       →  printable summary
 */
export const STEP_REGISTRY: readonly StepDefinition[] = [
  {
    id: 'preflight',
    title: 'Pre-flight & Bow Specs',
    goal: 'Confirm your bow matches manufacturer specs before we tune anything. New strings need ~100 arrows to settle in.',
  },
  {
    id: 'arrow_prep',
    title: 'Arrow Prep & Spin-Test',
    goal: "Spin-test the dozen, match-weight, index nocks, square inserts. The #1 rule — fix the arrows BEFORE you tune the bow. A bent shaft will sabotage every adjustment downstream.",
  },
  {
    id: 'bow_grip',
    title: 'Bow Grip Assessment',
    goal: 'Get the grip right BEFORE you tune. A sloppy or torquey grip creates phantom tuning problems — left/right misses, cam lean, peep rotation — that no rest adjustment will fix.',
  },
  {
    id: 'cam_timing',
    title: 'Cam Pre-Lean, Timing & Sync',
    goal: 'Three separate checks in order: (1) static cam lean at brace — set with yokes; (2) timing marks aligned against the cable; (3) sync — both stops hit at the exact same moment on draw. Pre-lean must come BEFORE sync.',
  },
  {
    id: 'tiller',
    title: 'Tiller Check',
    goal: 'Measure the distance from each limb bolt to the string. A consistent tiller (top vs bottom) means symmetric limb performance.',
  },
  {
    id: 'center_shot',
    title: 'Center Shot',
    goal: 'Set the arrow rest so the arrow tip aligns with the riser center per your manufacturer spec — typical starting point is 13/16" from riser.',
  },
  {
    id: 'nocking_d_loop',
    title: 'Nocking Point & D-Loop',
    goal: 'Set the nocking point ~1/8" above square so the arrow flies level off the rest. Install the D-loop with proper spacing above and below the arrow.',
  },
  {
    id: 'rest_branch',
    title: 'Rest Type',
    goal: 'Pick your rest type. Limb-driven and cable-driven rests time differently and each has its own setup procedure.',
    isBranchParent: true,
  },
  {
    id: 'rest_limb_driven',
    title: 'Limb-Driven Setup',
    goal: 'Tie the actuation cord to the bottom limb. Closer to limb tip = rest comes up earlier and stays up longer. Target: launcher supports arrow 50-70% of the power stroke.',
  },
  {
    id: 'rest_cable_driven',
    title: 'Cable-Driven Setup',
    goal: 'Clamp the activation cord to the down-traveling buss cable. Adjust clamp position so the rest reaches full-up at 50-70% of the power stroke.',
  },
  {
    id: 'paper_tune',
    title: 'Paper Tune',
    goal: 'Shoot a fletched arrow through butcher paper at 6 ft. A clean bullet hole means the bow is square. Tail-high, tail-low, tail-left, tail-right tears each tell you what to adjust.',
  },
  {
    id: 'bareshaft',
    title: 'Bareshaft Refinement',
    goal: 'Shoot an unfletched arrow alongside a fletched one. Same hole = your dynamic tune is good. Different hole = fine-tune rest left/right and nock height.',
  },
  {
    id: 'nock_tuning',
    title: 'Nock Tuning (Indexing)',
    goal: 'Rotate each arrow\'s nock until every shaft leaves the bow flexing the same way. The single biggest reason groups open up at distance with a "well tuned" bow is that each arrow is launching with a different dynamic-spine vector. Index them and they fly as a team.',
  },
  {
    id: 'sight_peep',
    title: 'Sight & Peep Calibration',
    goal: 'Peep size + 1st / 2nd / 3rd axis. The biggest gap between a paper-tuned compound and a bow that actually hits left at distance from a tree stand. The most-skipped step in a pro-shop tune.',
  },
  {
    id: 'walk_back',
    title: 'Walk-Back Tune',
    goal: 'Shoot the 20-yard pin at 20, 40, 50, and 60 yards. Left/right drift at distance means micro-adjust the rest. Vertical drift means check arrow spine or broadhead alignment.',
  },
  {
    id: 'broadhead_group',
    title: 'Broadhead Group',
    goal: 'Shoot fixed-blade broadheads alongside field points at 30+ yards. Same group = bow is fully tuned. Different groups = there\'s still tuning work to do.',
  },
  {
    id: 'final_report',
    title: 'Final Report',
    goal: 'Review every measurement, save the session, and export a printable tuning report.',
  },
] as const;

export function getStepDefinition(stepId: StepId): StepDefinition {
  const def = STEP_REGISTRY.find((s) => s.id === stepId);
  if (!def) throw new Error(`Unknown step: ${stepId}`);
  return def;
}
